import { useEffect, useState } from "react";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";

// Components
import Card from "components/Card";
import Button from "components/Button";
import Table from "components/Table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import ErrorBox from "components/ErrorBox";
import Backdrop from "components/Backdrop";

// API Helper
import {
  getClientUsersByClientIdAPI,
  getAllSystemUsersAPI,
  createClientUserLinkAPI,
  deleteClientUserLinkAPI,
  inviteClientUserAPI,
  toggleClientUserStatusAPI
} from "api/apiHelper";

// --- Types ---
interface Props {
  clientId: string;
}

interface ClientUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  job_title: string;
  status: 'PENDING' | 'ACTIVE' | 'INACTIVE';
  invitation_sent_at?: string;
  password_set_at?: string;
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  is_active: boolean;
  created_at: string;
}

interface SystemUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  permissions: string[];
}

const ClientUserAssignment = ({ clientId }: Props) => {
  // --- State ---
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Assign Modal State (for existing users)
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [eligibleUsers, setEligibleUsers] = useState<SystemUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [assigning, setAssigning] = useState<boolean>(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Invite Modal State (for new users)
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    job_title: ''
  });
  const [inviting, setInviting] = useState<boolean>(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Toggle Status Modal State
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
  const [itemToToggle, setItemToToggle] = useState<{ id: number; currentStatus: string } | null>(null);
  const [newStatus, setNewStatus] = useState<'ACTIVE' | 'INACTIVE'>("ACTIVE");
  const [togglingStatus, setTogglingStatus] = useState<boolean>(false);

  const columnHelper = createColumnHelper<ClientUser>();

  // --- 1. Fetch Existing Assignments ---
  const fetchClientUsers = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await getClientUsersByClientIdAPI(parseInt(clientId));
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setClientUsers(data);
    } catch (err: any) {
      console.error(err);
      setError("Failed to load client users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // --- 2. Handle "Assign Login" (for existing users) ---
  const handleOpenAssignModal = async () => {
    setAssignModalOpen(true);
    setAssignError(null);
    setSelectedUserId("");
    
    try {
      const response = await getAllSystemUsersAPI();
      const allCandidates: SystemUser[] = Array.isArray(response.data) ? response.data : response.data.results || [];
      const currentAssignedIds = new Set(clientUsers.filter(cu => cu.user).map(cu => cu.user!.id));
      const filtered = allCandidates.filter(user => !currentAssignedIds.has(user.id));
      setEligibleUsers(filtered);

      if (filtered.length === 0) {
        setAssignError("No eligible users found. All users are already assigned.");
      }
    } catch (err) {
      setAssignError("Failed to load available users.");
    }
  };

  // --- 3. Submit Assignment ---
  const handleAssign = async () => {
    if (!selectedUserId) return;
    
    try {
        setAssigning(true);
        // Call API to link user to client
        await createClientUserLinkAPI(parseInt(clientId), parseInt(selectedUserId));
        toast.success("User assigned to client successfully!");
        setAssignModalOpen(false);
        fetchClientUsers(); // Refresh list
    } catch (err: any) {
        setAssignError(err?.response?.data?.error || "Failed to assign user.");
    } finally {
        setAssigning(false);
    }
  };

  // --- 4. Delete Assignment ---
  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
        setDeleting(true);
        await deleteClientUserLinkAPI(itemToDelete);
        toast.success("Access revoked successfully.");
        setDeleteModalOpen(false);
        fetchClientUsers();
    } catch (err) {
        toast.error("Failed to remove user.");
    } finally {
        setDeleting(false);
    }
  };

  // --- 5. Handle "Invite User" (new user) ---
  const handleOpenInviteModal = () => {
    setInviteModalOpen(true);
    setInviteError(null);
    setInviteFormData({
      email: '',
      first_name: '',
      last_name: '',
      job_title: ''
    });
  };

  const handleInviteChange = (e: any) => {
    const { name, value } = e.target;
    setInviteFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // --- 6. Submit Invitation ---
  const handleSendInvitation = async () => {
    if (!inviteFormData.email || !inviteFormData.first_name || !inviteFormData.last_name) {
      setInviteError("Email, first name, and last name are required");
      return;
    }

    try {
        setInviting(true);
        setInviteError(null);
        
        await inviteClientUserAPI({
          client_id: parseInt(clientId),
          email: inviteFormData.email,
          first_name: inviteFormData.first_name,
          last_name: inviteFormData.last_name,
          job_title: inviteFormData.job_title
        });

        toast.success(`Invitation sent to ${inviteFormData.email}!`);
        setInviteModalOpen(false);
        fetchClientUsers();
    } catch (err: any) {
        const errorMsg = err?.response?.data?.email?.[0] || err?.response?.data?.error || "Failed to send invitation";
        setInviteError(errorMsg);
    } finally {
        setInviting(false);
    }
  };

  // --- 7. Toggle Status ---
  const handleToggleStatus = async () => {
    if (!itemToToggle) return;
    try {
        setTogglingStatus(true);
        await toggleClientUserStatusAPI(itemToToggle.id, newStatus);
        toast.success(`Status changed to ${newStatus}`);
        setStatusModalOpen(false);
        fetchClientUsers();
    } catch (err: any) {
        toast.error("Failed to update status.");
    } finally {
        setTogglingStatus(false);
    }
  };

  const openStatusToggle = (id: number, currentStatus: string) => {
    setItemToToggle({ id, currentStatus });
    setNewStatus(currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
    setStatusModalOpen(true);
  };

  // --- Table Configuration ---
  const columns: ColumnDef<ClientUser, any>[] = [
    columnHelper.accessor("email", {
        header: () => <TableHeader>Email</TableHeader>,
        cell: (info) => <TableCell className="font-semibold">{info.getValue()}</TableCell>
    }),
    columnHelper.accessor("first_name", {
        header: () => <TableHeader>Name</TableHeader>,
        cell: (info) => <TableCell>{info.getValue()} {info.row.original.last_name}</TableCell>
    }),
    columnHelper.accessor("job_title", {
        header: () => <TableHeader>Job Title</TableHeader>,
        cell: (info) => <TableCell>{info.getValue() || "-"}</TableCell>
    }),
    columnHelper.accessor("status", {
        header: () => <TableHeader>Status</TableHeader>,
        cell: (info) => {
          const status = info.getValue();
          const statusColors = {
            PENDING: "bg-yellow-100 text-yellow-800",
            ACTIVE: "bg-green-100 text-green-800",
            INACTIVE: "bg-red-100 text-red-800"
          };
          return (
            <TableCell>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}`}>
                {status}
              </span>
            </TableCell>
          );
        }
    }),
    columnHelper.display({
        id: "actions",
        header: () => <TableHeader>Actions</TableHeader>,
        cell: (info) => (
            <TableCell>
              <div className="flex items-center gap-2">
                {info.row.original.status !== 'PENDING' && (
                  <button
                    onClick={() => openStatusToggle(info.row.original.id, info.row.original.status)}
                    className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
                    title={info.row.original.status === 'ACTIVE' ? "Deactivate" : "Activate"}
                  >
                    <i className={`fas fa-${info.row.original.status === 'ACTIVE' ? 'ban' : 'check'} text-blue-600 dark:text-blue-400 text-sm`}></i>
                  </button>
                )}
                <button
                    onClick={() => {
                        setItemToDelete(info.row.original.id);
                        setDeleteModalOpen(true);
                    }}
                    className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                    title="Revoke Access"
                >
                    <i className="fas fa-trash text-red-600 dark:text-red-400 text-sm"></i>
                </button>
              </div>
            </TableCell>
        )
    })
  ];

  return (
    <div className="space-y-4">
        {error && <ErrorBox errorMessage={error} />}

        <Card extra="w-full h-full p-4 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                        Manage Client Users
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Invite new users or assign existing ones to access the client portal.
                    </p>
                </div>
                <Button
                  onClick={handleOpenInviteModal}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-2 dark:bg-green-600 dark:hover:bg-green-700"
                >
                  <i className="fas fa-envelope"></i>
                  Invite User
                </Button>
            </div>

            {loading && clientUsers.length === 0 ? (
                <div className="py-10 text-center"><Backdrop show={true} /></div>
            ) : (
                <Table<ClientUser>
                    tableData={clientUsers}
                    columns={columns}
                    tableHeading="Client Users"
                    totalCount={clientUsers.length}
                    currentPage={1} 
                    pageSize={10}
                />
            )}
        </Card>

        {/* --- INVITE MODAL --- */}
        {inviteModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setInviteModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl w-full max-w-lg p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setInviteModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
            
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
              Invite Client User
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Send an invitation to a new user. They will receive an email with a setup link.
            </p>

            {inviteError && <ErrorBox errorMessage={inviteError} />}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={inviteFormData.email}
                  onChange={handleInviteChange}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={inviteFormData.first_name}
                    onChange={handleInviteChange}
                    placeholder="John"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={inviteFormData.last_name}
                    onChange={handleInviteChange}
                    placeholder="Doe"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Job Title (Optional)
                </label>
                <input
                  type="text"
                  name="job_title"
                  value={inviteFormData.job_title}
                  onChange={handleInviteChange}
                  placeholder="Project Manager"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setInviteModalOpen(false)}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={inviting}
                className={`px-4 py-2 rounded-md text-white font-semibold ${
                  inviting
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-600"
                }`}
              >
                {inviting ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </div>
        </div>
      )}

        {/* --- ASSIGN MODAL (existing users) --- */}
        {assignModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setAssignModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl w-full max-w-lg p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setAssignModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
            
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
              Assign Existing User
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Select an existing system user to give portal access.
            </p>

            {assignError && <ErrorBox errorMessage={assignError} />}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Select User
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">-- Select a User --</option>
                {eligibleUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.username})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAssignModalOpen(false)}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedUserId || assigning}
                className={`px-4 py-2 rounded-md text-white font-semibold ${
                  !selectedUserId || assigning
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-brand-500 hover:bg-brand-600"
                }`}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- DELETE CONFIRMATION MODAL --- */}
      {deleteModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setDeleteModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
             <div className="flex items-center gap-3 mb-4">
                 <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <i className="fas fa-exclamation-triangle text-red-600"></i>
                 </div>
                 <h3 className="text-lg font-bold text-navy-700 dark:text-white">Revoke Access?</h3>
             </div>
             
             <p className="text-gray-600 dark:text-gray-300 mb-6">
                 Are you sure you want to remove this user? They will no longer be able to log in.
             </p>
             <div className="flex justify-end gap-3">
                 <button onClick={() => setDeleteModalOpen(false)} className="px-4 py-2 border rounded-lg text-gray-600 dark:text-gray-300 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700">Cancel</button>
                 <button 
                    onClick={handleDelete} 
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                 >
                    {deleting ? "Removing..." : "Remove"}
                 </button>
             </div>
          </div>
        </div>
      )}

      {/* --- STATUS TOGGLE MODAL --- */}
      {statusModalOpen && itemToToggle && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setStatusModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
             <div className="flex items-center gap-3 mb-4">
                 <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <i className="fas fa-toggle-on text-blue-600"></i>
                 </div>
                 <h3 className="text-lg font-bold text-navy-700 dark:text-white">Change Status?</h3>
             </div>
             
             <p className="text-gray-600 dark:text-gray-300 mb-6">
                 Change status to <strong>{newStatus}</strong>? {newStatus === 'INACTIVE' ? 'This user will no longer be able to log in.' : 'This user will be able to log in.'}
             </p>
             <div className="flex justify-end gap-3">
                 <button onClick={() => setStatusModalOpen(false)} className="px-4 py-2 border rounded-lg text-gray-600 dark:text-gray-300 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700">Cancel</button>
                 <button 
                    onClick={handleToggleStatus} 
                    disabled={togglingStatus}
                    className={`px-4 py-2 text-white rounded-lg ${newStatus === 'ACTIVE' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                 >
                    {togglingStatus ? "Updating..." : "Change Status"}
                 </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientUserAssignment;